import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import { MatCardModule } from "@angular/material/card";
import { MatDividerModule } from "@angular/material/divider";
import { MatExpansionModule } from "@angular/material/expansion";
import { MillisecondsToTimePipe } from "src/app/shared/pipes/milliseconds-to-time.pipe";
import { LocalDatetimePipe } from "src/app/shared/pipes/local-datetime.pipe";
import { MEASURED_DATA_DETAILS_I18N } from "../../measured-data-details.i18n";
import { GOST_I18N } from "../../../cards/recordings/gost.i18n";

type GostSummaryCard =
  | {
      key: "fils";
      title: string;
      score: number | null;
      level: null;
      abnormalCount: number;
      recommendation: string | null;
    }
  | {
      key: "sc";
      title: string;
      score: null;
      level: null;
      abnormalCount: number;
      maxVolume: number | null;
      hasAbnormal: boolean;
    }
  | {
      key: "gc" | "oc";
      title: string;
      score: number | null;
      level: number | null;
      abnormalCount: number;
    };

type MwstScoreItem = {
  value: number;
  description: string;
  checked: boolean;
};

@Component({
  selector: "app-measured-data-summary-card",
  templateUrl: "./measured-data-summary-card.component.html",
  styleUrls: ["./measured-data-summary-card.component.scss"],
  imports: [
    MatCardModule,
    MatDividerModule,
    MatExpansionModule,
    MillisecondsToTimePipe,
    LocalDatetimePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasuredDataSummaryCardComponent implements OnChanges {
  protected readonly i18n = MEASURED_DATA_DETAILS_I18N;

  @Input() recordData: any;
  @Input() recordtype: number | null = null;
  // hide cough count when createdAt is on/before this date
  @Input() hideCoughCountBefore: string | Date | null = null;
  // Backward compatibility (previous name). Hides when createdAt is on/after this date.
  @Input() hideCoughCountAfter: string | Date | null = null;

  @Output() sectionRequested = new EventEmitter<"fils" | "sc" | "gc" | "oc">();

  readonly abnormalPresentLabel = MEASURED_DATA_DETAILS_I18N.summaryCard.abnormal.present;
  readonly abnormalAbsentLabel = MEASURED_DATA_DETAILS_I18N.summaryCard.abnormal.absent;

  gostSummaryCards: GostSummaryCard[] = [];
  recommendedFoods: { name: string; rank: number; description?: string }[] = [];
  mwstScoreItems: MwstScoreItem[] = [];

  private readonly mwstScoreCandidates: Omit<MwstScoreItem, "checked">[] =
    GOST_I18N.mwst.results.map((r) => ({
      value: r.value,
      description: r.description,
    }));

  private readonly foodTestScoreCandidates: Omit<MwstScoreItem, "checked">[] =
    GOST_I18N.foodTest.results.map((r) => ({
      value: r.value,
      description: r.description,
    }));

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["recordData"] || changes["recordtype"]) {
      this.gostSummaryCards = this.buildGostSummaryCards();
      this.recommendedFoods = this.buildRecommendedFoods();
      this.mwstScoreItems = this.buildMwstScoreItems();
    }
  }

  isZeroTimeGost(): boolean {
    if (this.recordtype !== 7) {
      return false;
    }
    const totalTime = this.recordData?.data?.totalTime;
    const wavDuration = Number(this.recordData?.data?.wavDuration ?? 0);
    return (
      totalTime == null ||
      totalTime === "" ||
      totalTime === "00:00:00" ||
      !Number.isFinite(wavDuration) ||
      wavDuration <= 0
    );
  }

  shouldShowMeasurementSummary(): boolean {
    if (!this.recordData) {
      return false;
    }
    // MWST-FTG(3)とSSTP(6)は従来通り非表示。VS(4)は表示対象に含める。
    return !this.isZeroTimeGost() && ![3, 6].includes(this.recordtype ?? -99);
  }

  shouldShowAiDisclaimer(): boolean {
    return this.shouldShowMeasurementSummary() || this.isMwstOrFoodTest();
  }

  shouldShowMeasurementTime(): boolean {
    if (!this.recordData) {
      return false;
    }
    return !this.isZeroTimeGost();
  }

  shouldShowCoughCount(): boolean {
    if (!this.recordData || !this.shouldShowMeasurementSummary()) {
      return false;
    }

    const createdAt = new Date(this.recordData?.data?.createdAt);
    if (!Number.isFinite(createdAt.getTime())) {
      return true;
    }

    if (this.hideCoughCountBefore) {
      const thresholdBefore = new Date(this.hideCoughCountBefore);
      if (
        Number.isFinite(thresholdBefore.getTime()) &&
        createdAt <= thresholdBefore
      ) {
        return false;
      }
    }

    if (this.hideCoughCountAfter) {
      const thresholdAfter = new Date(this.hideCoughCountAfter);
      if (
        Number.isFinite(thresholdAfter.getTime()) &&
        createdAt >= thresholdAfter
      ) {
        return false;
      }
    }

    return true;
  }

  hasGostSummary(): boolean {
    const d = this.recordData?.data;
    return (
      this.recordtype === 7 &&
      !!d?.gostSCData &&
      !!d?.gostGCData &&
      !!d?.gostOCData
    );
  }

  private isMwstOrFoodTest(): boolean {
    const type = this.recordData?.data?.recordType;
    return [
      "MWST",
      "MWST_G",
      "MWST_G_DATA",
      "FOOD_TEST",
      "FOOD_TEST_G",
      "FOOD_TEST_G_DATA",
    ].includes(type ?? "");
  }

  private isFoodTest(): boolean {
    const type = this.recordData?.data?.recordType;
    return ["FOOD_TEST", "FOOD_TEST_G", "FOOD_TEST_G_DATA"].includes(
      type ?? "",
    );
  }

  private buildGostSummaryCards(): GostSummaryCard[] {
    if (!this.hasGostSummary()) {
      return [];
    }

    const d = this.recordData.data;
    const titles = this.i18n.summaryCard.gostTitle;
    return [
      {
        key: "fils" as const,
        title: titles.estimatedEatingAbility,
        score: d?.filsGrade ?? null,
        level: null,
        abnormalCount: 0,
        recommendation: d?.gostRecommendation ?? d?.filsRecommendation ?? null,
      },
      {
        key: "sc" as const,
        title: titles.waterSwallowTest,
        score: null,
        level: null,
        abnormalCount: 0,
        maxVolume: this.getMaxMeasuredVolume(d?.gostSCData),
        hasAbnormal: this.countAbnormalSc(d?.gostSCData) > 0,
      },
      {
        key: "gc" as const,
        title: titles.generalCondition,
        score: d?.gostGCData?.totalScore ?? null,
        level: d?.gostGCData?.level ?? null,
        abnormalCount: this.countAbnormalGcOc(d?.gostGCData),
      },
      {
        key: "oc" as const,
        title: titles.oralFunction,
        score: d?.gostOCData?.totalScore ?? null,
        level: d?.gostOCData?.level ?? null,
        abnormalCount: this.countAbnormalGcOc(d?.gostOCData),
      },
    ];
  }

  private buildMwstScoreItems(): MwstScoreItem[] {
    if (!this.isMwstOrFoodTest()) {
      return [];
    }

    const finalScore = Number(this.recordData?.data?.finalScore);
    if (!Number.isFinite(finalScore)) {
      return [];
    }

    const candidates = this.isFoodTest()
      ? this.foodTestScoreCandidates
      : this.mwstScoreCandidates;

    const matched = candidates.find((item) => item.value === finalScore);
    if (!matched) {
      return [];
    }

    return [{ ...matched, checked: true }];
  }

  onSummaryDetailClick(key: "fils" | "sc" | "gc" | "oc") {
    this.sectionRequested.emit(key);
  }

  private countAbnormalSc(scData: any): number {
    if (!scData) {
      return 0;
    }

    const withOrWithout = [
      ...(scData.withoutThicknessSteps ?? []),
      ...(scData.withThicknessSteps ?? []),
    ];

    let count = 0;
    for (const step of withOrWithout) {
      for (const round of step?.rounds ?? []) {
        if (!round?.isMeasured) {
          continue;
        }
        const result = this.evaluateScRound(round);
        if (
          result?.testResult &&
          result.testResult !== "radio_button_unchecked"
        ) {
          count++;
        }
      }
    }

    return count;
  }

  private evaluateScRound(round: any) {
    const convert = (val: string) =>
      val === "TP" || val === "FN" ? "TP" : "TN";
    const testResultDisp = {
      good: "radio_button_unchecked",
      acceptable: "△",
      bad: "clear",
      notMeasured: "-",
    };

    const converted: any = {};
    converted.swallowResult = convert(round.swallowResult);
    converted.coughResult = convert(round.coughResult);
    converted.swallowDelay = convert(round.swallowDelay);
    converted.abnormalSound = convert(round.abnormalSound);
    converted.multiSwallows = convert(round.multiSwallows);
    converted.throatClear = convert(round.throatClear);
    converted.otherAbbration = convert(round.otherAbbration);

    converted.otherAbnormality = null;
    if (
      converted.swallowDelay === "TP" ||
      converted.abnormalSound === "TP" ||
      converted.multiSwallows === "TP" ||
      converted.throatClear === "TP" ||
      converted.otherAbbration === "TP"
    ) {
      converted.otherAbnormality = "TP";
    }

    converted.testResult = testResultDisp.good;
    if (converted.swallowResult === "TN") {
      converted.testResult = testResultDisp.bad;
    }
    if (converted.coughResult === "TP") {
      converted.testResult = testResultDisp.bad;
    }
    if (
      converted.otherAbnormality === "TP" &&
      converted.testResult === testResultDisp.good
    ) {
      converted.testResult = testResultDisp.acceptable;
    }
    if (
      converted.swallowResult == null &&
      converted.coughResult == null &&
      converted.otherAbnormality == null
    ) {
      converted.testResult = testResultDisp.notMeasured;
    }
    return converted;
  }

  private countAbnormalGcOc(data: any): number {
    if (!data?.metrics?.length) {
      return 0;
    }
    return data.metrics.filter((m: any) => m?.score != null && m.score < 0)
      .length;
  }

  private getMaxMeasuredVolume(scData: any): number | null {
    if (!scData) {
      return null;
    }

    const steps = [
      ...(scData.withoutThicknessSteps ?? []),
      ...(scData.withThicknessSteps ?? []),
    ];

    let max: number | null = null;
    for (const step of steps) {
      if (!step?.amount) continue;
      const measured = (step.rounds ?? []).some((r: any) => r?.isMeasured);
      if (measured) {
        if (max === null || step.amount > max) {
          max = step.amount;
        }
      }
    }

    return max;
  }

  private buildRecommendedFoods(): {
    name: string;
    rank: number;
    description?: string;
  }[] {
    const foods = this.recordData?.data?.PrimarilySelectedFoods?.selectedFoods;
    const list: { name: string; rank: number; description?: string }[] = [];

    if (Array.isArray(foods)) {
      list.push(
        ...foods
          .filter((f: any) => !!f?.name)
          .map((f: any) => ({
            name: f.name,
            rank: Number(f.rank ?? 0),
            description: f.description,
          })),
      );
    }

    const fallbackName =
      this.recordData?.data?.gostRecommendation ??
      this.recordData?.data?.filsRecommendation;
    if (!list.length && fallbackName) {
      list.push({ name: fallbackName, rank: 1 });
    }

    return list.sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0));
  }

  getRecommendSizeClass(text: string | null | undefined): string {
    if (!text) {
      return "";
    }
    const len = String(text).length;
    if (len > 18) {
      return "recommend--xs";
    }
    if (len > 12) {
      return "recommend--sm";
    }
    return "";
  }
}
